import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Card,
    IconButton,
    TextField,
    Typography,
    Fab,
    Tooltip,
    Paper,
    Chip,
    CircularProgress,
    Avatar,
    Fade,
    Zoom,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    // ...existing code...
} from '@mui/material';
import {
    Send as SendIcon,
    AttachFile as AttachFileIcon,
    Close as CloseIcon,
    Chat as ChatIcon,
    SmartToy as BotIcon,
    Build as BuildIcon,
    Mail as MailIcon,
    History as ClockIcon,
    // Storage as DatabaseIcon,
    PauseCircleOutline as PauseIcon,
    PlayCircleOutline as PlayIcon,
    Mic as MicIcon,
    Stop as StopIcon,
    KeyboardArrowDown as MinimizeIcon,
    DeleteSweep as ClearIcon,
    ThumbUp as ThumbUpIcon,
    ThumbDown as ThumbDownIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import Markdown from 'react-markdown';
import { colors } from '../theme';
import { useAuthStore } from '../store/authStore';

interface Message {
    role: 'user' | 'assistant' | 'system';
    content: string;
    files?: File[];
    fileNames?: string[];
    toolsUsed?: string[];
    timestamp: Date;
    messageId?: number;
    feedbackGiven?: boolean;
}

// Tool interface removed — available-tools UI eliminated

interface InterruptData {
    instruction: string;
    tool_name: string;
    content: string;
    action: string;
}


interface ChatWidgetProps {
    adminId?: string;
    backendUrl?: string;
}

const InterruptReviewDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    interruptData: InterruptData | null;
    editedContent: string;
    onEditedContentChange: (content: string) => void;
    onContinue: (content: string) => void;
}> = ({ open, onClose, interruptData, editedContent, onEditedContentChange, onContinue }) => {
    if (!interruptData) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Workflow Paused for Review</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    {interruptData.instruction}
                </DialogContentText>
                <TextField
                    fullWidth
                    multiline
                    rows={10}
                    value={editedContent}
                    onChange={(e) => onEditedContentChange(e.target.value)}
                    variant="outlined"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={() => onContinue(editedContent)} variant="contained">
                    Continue
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const ChatWidget: React.FC<ChatWidgetProps> = ({
    adminId = process.env.REACT_APP_ADMIN_ID,
    backendUrl = process.env.REACT_APP_BACKEND_URL
}) => {
    const { user } = useAuthStore();
    // Scope all storage keys per-user so sessions never bleed across accounts
    const userStorageId = `${adminId}_${user?.id || 'anon'}`;
    const [messages, setMessages] = useState<Message[]>(() => {
        const savedMessages = localStorage.getItem(`chatbot_messages_${userStorageId}`);
        if (savedMessages) {
            try {
                return JSON.parse(savedMessages).map((msg: any) => ({
                    ...msg,
                    timestamp: new Date(msg.timestamp)
                }));
            } catch (e) {
                console.error("Failed to parse saved messages", e);
                return [];
            }
        }
        return [];
    });
    // SECURITY FIX: Removed console.log that exposed adminId and backendUrl

    const [input, setInput] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isOpen, setIsOpen] = useState(() => {
        const savedIsOpen = localStorage.getItem(`chatbot_isOpen_${userStorageId}`);
        return savedIsOpen === 'true';
    });
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [endUserEmail, setEndUserEmail] = useState('');
    const [accessStatus, setAccessStatus] = useState<'pending' | 'approved' | 'checking' | 'none'>('checking');
    const [threadId, setThreadId] = useState<string | null>(() => {
        return localStorage.getItem(`chatbot_threadId_${userStorageId}`);
    });
    const [interruptData, setInterruptData] = useState<InterruptData | null>(null);
    const [editedContent, setEditedContent] = useState<string>('');
    const [showInterruptDialog, setShowInterruptDialog] = useState(false);
    const [browserAutomationEnabled, setBrowserAutomationEnabled] = useState(false);

    // Feedback state
    const [correctionInputId, setCorrectionInputId] = useState<number | null>(null);
    const [correctionValue, setCorrectionValue] = useState("");

    // Voice recording state
    const [isRecording, setIsRecording] = useState(false);
    const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        return () => { abortControllerRef.current?.abort(); };
    }, []);

    const handleCancelRequest = () => {
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        setIsLoading(false);
    };

    const handleClearChat = () => {
        setMessages([]);
        localStorage.removeItem(`chatbot_messages_${userStorageId}`);
        toast.info('Chat history cleared.');
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
        localStorage.setItem(`chatbot_messages_${userStorageId}`, JSON.stringify(messages));
    }, [messages, userStorageId]);

    useEffect(() => {
        localStorage.setItem(`chatbot_isOpen_${userStorageId}`, String(isOpen));
    }, [isOpen, userStorageId]);

    useEffect(() => {
        if (threadId) {
            localStorage.setItem(`chatbot_threadId_${userStorageId}`, threadId);
        }
    }, [threadId, userStorageId]);

    useEffect(() => {
        if (isOpen) {
            const storedEmail = localStorage.getItem(`chatbot_email_${userStorageId}`);
            if (storedEmail) {
                setEndUserEmail(storedEmail);
                setAccessStatus('checking');
            } else if (user?.email) {
                setEndUserEmail(user.email);
                // Auto-request access if we have the user's email but no stored session
                handleRequestAccess(user.email);
            } else {
                setAccessStatus('none');
            }
        }
    }, [isOpen, adminId, user]);

    useEffect(() => {
        let intervalId: NodeJS.Timeout;

        const checkAccess = async () => {
            if (!endUserEmail || !adminId || accessStatus === 'approved' || !isOpen) return;

            try {
                const response = await fetch(
                    `${backendUrl}/check_access_status/${adminId}?end_user_email=${endUserEmail}`
                );
                if (!response.ok) throw new Error('Failed to check access status');
                const data = await response.json();
                setAccessStatus(data.status);

                if (data.status === 'approved' && intervalId) {
                    clearInterval(intervalId);
                }
            } catch (error) {
                console.error('Error checking access status:', error);
            }
        };

        if (isOpen && (accessStatus === 'pending' || accessStatus === 'checking')) {
            checkAccess(); // Initial check
            intervalId = setInterval(checkAccess, 5000);
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [endUserEmail, adminId, accessStatus, backendUrl, isOpen]);

    useEffect(() => {
        // Fetch chatbot tools to check if browser automation is enabled
        const fetchChatbotTools = async () => {
            if (accessStatus !== 'approved' || !adminId) return;

            try {
                const response = await fetch(`${backendUrl}/chatbot_tools/${adminId}`);
                if (response.ok) {
                    const tools = await response.json();
                    console.log("Fetched chatbot tools:", tools);
                    // check whether browser automation is enabled
                    const hasBrowserAutomation =
                        Array.isArray(tools.tools) &&
                        tools.tools.some((tool: any) => tool.name === "Browser Automation");

                    console.log("hasBrowserAutomation =", hasBrowserAutomation);
                    setBrowserAutomationEnabled(hasBrowserAutomation);
                    console.log('Browser automation enabled:', hasBrowserAutomation);
                }
            } catch (error) {
                console.error('Error fetching chatbot tools:', error);
            }
        };

        fetchChatbotTools();
    }, [accessStatus, adminId, backendUrl]);

    // tool icon mapping removed (tools UI removed)

    // Browser Monitoring Logic
    const extractFormFields = () => {
        const formFields: any[] = [];

        // Find all interactive form elements
        const inputs = document.querySelectorAll('input, textarea, select');

        inputs.forEach((element, index) => {
            const input = element as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

            // Skip hidden, submit, and button inputs
            const type = (input as HTMLInputElement).type;
            if (type === 'hidden' || type === 'submit' || type === 'button' || type === 'image') {
                return;
            }

            // Build a reliable CSS selector
            let selector = '';
            if (input.id) {
                //selector = `#${input.id}`;
                selector = `#${CSS.escape(input.id)}`;
            } else if (input.name) {
                selector = `${input.tagName.toLowerCase()}[name="${input.name}"]`;
            } else {
                // Use a combination of tag, type, and placeholder
                selector = input.tagName.toLowerCase();
                if (type) selector += `[type="${type}"]`;
                // Only input and textarea have placeholder
                const inputOrTextarea = input as HTMLInputElement | HTMLTextAreaElement;
                if ((input.tagName.toLowerCase() === 'input' || input.tagName.toLowerCase() === 'textarea') && inputOrTextarea.placeholder) {
                    selector += `[placeholder="${inputOrTextarea.placeholder}"]`;
                }
            }

            // Get associated label
            let label = '';
            if (input.id) {
                const labelElement = document.querySelector(`label[for="${input.id}"]`);
                if (labelElement) {
                    label = labelElement.textContent?.trim() || '';
                }
            }
            // Try to find label by proximity if not found
            if (!label) {
                const parent = input.closest('label');
                if (parent) {
                    label = parent.textContent?.replace((input as HTMLInputElement).value || '', '').trim() || '';
                }
            }
            // Try to find label in previous sibling
            if (!label) {
                let prev = input.previousElementSibling;
                if (prev && prev.tagName === 'LABEL') {
                    label = prev.textContent?.trim() || '';
                }
            }

            const inputOrTextarea = input as HTMLInputElement | HTMLTextAreaElement;
            const fieldInfo: any = {
                selector: selector,
                type: input.tagName.toLowerCase(),
                inputType: type || 'text',
                id: input.id || null,
                name: (input as HTMLInputElement).name || null,
                placeholder: (input.tagName.toLowerCase() === 'input' || input.tagName.toLowerCase() === 'textarea') ? inputOrTextarea.placeholder || null : null,
                label: label || null,
                value: (input as HTMLInputElement).value || '',
            };

            // For select elements, include options
            if (input.tagName.toLowerCase() === 'select') {
                const selectElement = input as HTMLSelectElement;
                fieldInfo.options = Array.from(selectElement.options).map(opt => ({
                    value: opt.value,
                    text: opt.text
                }));
            }

            formFields.push(fieldInfo);
        });

        return formFields;
    };

    const getPageContentWithoutChatWidget = () => {
        // Clone the body to avoid modifying the actual DOM
        const bodyClone = document.body.cloneNode(true) as HTMLElement;

        // Remove all elements with data-chat-widget attribute
        const chatElements = bodyClone.querySelectorAll('[data-chat-widget="true"]');
        chatElements.forEach(el => el.remove());

        return bodyClone.innerText.substring(0, 5000);
    };

    const sendBrowserUpdate = async () => {
        if (!endUserEmail || !backendUrl) return;

        try {
            const formFields = extractFormFields();

            // Get page content excluding the chat widget
            const pageContent = getPageContentWithoutChatWidget();

            const updateData = {
                user_email: endUserEmail,
                url: window.location.href,
                title: document.title,
                dom_summary: pageContent,
                form_fields: formFields,
                timestamp: Date.now() / 1000
            };

            await fetch(`${backendUrl}/monitor/update`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updateData)
            });
        } catch (error) {
            // Fail silently to not disrupt user experience
            console.error("Failed to send browser update:", error);
        }
    };

    useEffect(() => {
        console.log('Setting up browser monitoring...', { endUserEmail, browserAutomationEnabled });

        // Only monitor if we have a user email AND browser automation is enabled
        if (endUserEmail && browserAutomationEnabled) {
            // Send initial update
            sendBrowserUpdate();

            // Poll for changes every 10 seconds
            // This captures both URL changes and DOM content changes
            const interval = setInterval(sendBrowserUpdate, 10000);

            return () => clearInterval(interval);
        }
    }, [endUserEmail, backendUrl, browserAutomationEnabled]);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files);
            setFiles((prev) => [...prev, ...newFiles]);
        }
    };

    const removeFile = (index: number) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files) {
            const newFiles = Array.from(e.dataTransfer.files);
            setFiles((prev) => [...prev, ...newFiles]);
        }
    };

    const handleRequestAccess = async (emailToUse?: string) => {
        const email = emailToUse || endUserEmail;
        if (!email.trim() || !email.includes('@')) {
            toast.error('Please enter a valid email address.');
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(`${backendUrl}/request_access/${adminId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ end_user_email: email }),
            });

            if (!response.ok) throw new Error('Failed to request access');
            const data = await response.json();
            localStorage.setItem(`chatbot_email_${userStorageId}`, email);
            if (!emailToUse) {
                // Only update state if called without arg (manual) or if we want to sync
                setEndUserEmail(email);
            }
            setAccessStatus('pending');
            // toast.success(data.message || 'Your access request has been submitted.');
        } catch (error) {
            console.error("Error requesting access:", error);
            // toast.error('Failed to submit access request. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const sendMessage = async () => {
        if ((!input.trim() && files.length === 0) || accessStatus !== 'approved') return;

        const userMessage: Message = {
            role: 'user',
            content: input,
            fileNames: files.map(f => f.name),
            timestamp: new Date(),
        };
        const raw = localStorage.getItem('auth-storage') || '';
        const parsed = JSON.parse(raw);

        // access token
        const accessToken = parsed.state.token.access_token;

        const moduleID = localStorage.getItem('selectedModuleId') || '';
        const client_context = {
            module_id: moduleID,
            access_token: accessToken,
        };
        // SECURITY FIX: Removed console.log that exposed access token
        setMessages((prev) => [...prev, userMessage]);
        const currentInput = input;
        const currentFiles = files;
        setInput('');
        setFiles([]);
        setIsLoading(true);

        const executeActionsSequentially = async (actions: any[]) => {
            for (let i = 0; i < actions.length; i++) {
                const clientAction = actions[i];
                console.log(`Executing action ${i + 1}/${actions.length}:`, clientAction);

                if (clientAction.type === 'navigate') {
                    const url = clientAction.payload.url;
                    setMessages((prev: Message[]) => [...prev, {
                        role: 'system',
                        content: `🔄 Navigating to ${url}...`,
                        timestamp: new Date(),
                    }]);
                    setTimeout(() => { window.location.href = url; }, 1000);
                    return;

                } else if (clientAction.type === 'highlight') {
                    const selector = clientAction.payload.selector;
                    try {
                        const findElement = (sel: string): Element | null => {
                            if (sel.includes(':contains(')) {
                                const match = sel.match(/([a-zA-Z0-9_\-\.#\s>+~]+)?:contains\("?(.*?)\"?\)/);
                                if (match) {
                                    const [_, tagSelector, text] = match;
                                    const baseSelector = tagSelector ? tagSelector.trim() : '*';
                                    const elements = document.querySelectorAll(baseSelector);
                                    let bestMatch: Element | null = null;
                                    let minLength = Infinity;
                                    for (let j = 0; j < elements.length; j++) {
                                        const el = elements[j];
                                        if (el.textContent?.includes(text)) {
                                            if (el.textContent.length < minLength) {
                                                minLength = el.textContent.length;
                                                bestMatch = el;
                                            }
                                        }
                                    }
                                    return bestMatch;
                                }
                            }
                            return document.querySelector(sel);
                        };
                        const element = findElement(selector);
                        if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            const rect = element.getBoundingClientRect();
                            const overlay = document.createElement('div');
                            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                            const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
                            overlay.style.cssText = `position:absolute;top:${rect.top + scrollTop}px;left:${rect.left + scrollLeft}px;width:${rect.width}px;height:${rect.height}px;border:3px solid #FFD700;box-shadow:0 0 20px rgba(255,215,0,0.8);background:rgba(255,215,0,0.1);z-index:2147483647;pointer-events:none;border-radius:4px;transition:opacity 0.5s ease`;
                            document.body.appendChild(overlay);
                            setTimeout(() => { overlay.style.opacity = '0'; setTimeout(() => { document.body.contains(overlay) && document.body.removeChild(overlay); }, 500); }, 3000);
                        }
                    } catch (e) { console.error("Error highlighting element:", e); }

                } else if (clientAction.type === 'fill_form') {
                    const { selector, value, action } = clientAction.payload;
                    try {
                        const element = document.querySelector(selector) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
                        if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            const rect = element.getBoundingClientRect();
                            const overlay = document.createElement('div');
                            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                            const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
                            overlay.style.cssText = `position:absolute;top:${rect.top + scrollTop}px;left:${rect.left + scrollLeft}px;width:${rect.width}px;height:${rect.height}px;border:2px solid #4CAF50;box-shadow:0 0 10px rgba(76,175,80,0.6);background:rgba(76,175,80,0.1);z-index:2147483647;pointer-events:none;border-radius:4px;transition:opacity 0.3s ease`;
                            document.body.appendChild(overlay);
                            await new Promise(resolve => setTimeout(resolve, 500));
                            const elementType = element.tagName.toLowerCase();
                            const inputType = (element as HTMLInputElement).type?.toLowerCase();
                            if (action === 'check' || action === 'uncheck') {
                                if (inputType === 'checkbox' || inputType === 'radio') {
                                    (element as HTMLInputElement).checked = (action === 'check');
                                    element.dispatchEvent(new Event('change', { bubbles: true }));
                                }
                            } else if (action === 'select' || elementType === 'select') {
                                if (elementType === 'select') {
                                    (element as HTMLSelectElement).value = value;
                                    element.dispatchEvent(new Event('change', { bubbles: true }));
                                }
                            } else if (elementType === 'input' || elementType === 'textarea') {
                                const setter = Object.getOwnPropertyDescriptor(
                                    elementType === 'input' ? window.HTMLInputElement.prototype : window.HTMLTextAreaElement.prototype, 'value'
                                )?.set;
                                setter ? setter.call(element, value) : (element.value = value);
                                element.dispatchEvent(new Event('input', { bubbles: true }));
                                element.dispatchEvent(new Event('change', { bubbles: true }));
                            }
                            setTimeout(() => { overlay.style.opacity = '0'; setTimeout(() => { document.body.contains(overlay) && document.body.removeChild(overlay); }, 300); }, 1000);
                        } else {
                            setMessages((prev: Message[]) => [...prev, { role: 'system', content: `⚠️ Could not find form field: ${selector}`, timestamp: new Date() }]);
                        }
                    } catch (e) {
                        setMessages((prev: Message[]) => [...prev, { role: 'system', content: `❌ Error filling form field: ${e}`, timestamp: new Date() }]);
                    }
                    if (i < actions.length - 1) await new Promise(resolve => setTimeout(resolve, 600));
                }
            }
        };

        try {
            const formData = new FormData();
            formData.append('end_user_email', endUserEmail);
            formData.append('message', currentInput);
            formData.append('client_context', JSON.stringify(client_context));
            if (threadId) {
                formData.append('thread_id', threadId);
            }

            currentFiles.forEach((file) => {
                formData.append('files', file);
            });
            const controller = new AbortController();
            abortControllerRef.current = controller;

            const response = await fetch(`${backendUrl}/chat/${adminId}`, {
                method: 'POST',
                body: formData,
                signal: controller.signal,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Streaming path: backend sends SSE (text/event-stream)
            const contentType = response.headers.get('content-type') || '';
            if (contentType.includes('text/event-stream') && response.body) {
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let sseBuffer = '';
                let streamingStarted = false;

                try {
                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;
                        sseBuffer += decoder.decode(value, { stream: true });
                        const lines = sseBuffer.split('\n');
                        sseBuffer = lines.pop() || '';
                        for (const line of lines) {
                            if (!line.startsWith('data: ')) continue;
                            const raw = line.slice(6).trim();
                            if (!raw || raw === '[DONE]') continue;
                            try {
                                const evt = JSON.parse(raw);

                                if (evt.type === 'chunk' && evt.content) {
                                    if (!streamingStarted) {
                                        streamingStarted = true;
                                        setMessages((prev: Message[]) => [...prev, {
                                            role: 'assistant',
                                            content: '',
                                            timestamp: new Date(),
                                        }]);
                                    }
                                    setMessages((prev: Message[]) => {
                                        const updated = [...prev];
                                        const last = updated[updated.length - 1];
                                        if (last?.role === 'assistant') {
                                            updated[updated.length - 1] = { ...last, content: last.content + evt.content };
                                        }
                                        return updated;
                                    });

                                } else if (evt.type === 'done') {
                                    if (evt.thread_id) setThreadId(evt.thread_id);
                                    if (streamingStarted) {
                                        setMessages((prev: Message[]) => {
                                            const updated = [...prev];
                                            const last = updated[updated.length - 1];
                                            if (last?.role === 'assistant') {
                                                updated[updated.length - 1] = { ...last, messageId: evt.message_id, toolsUsed: evt.tools_used };
                                            }
                                            return updated;
                                        });
                                    } else if (evt.reply) {
                                        setMessages((prev: Message[]) => [...prev, {
                                            role: 'assistant',
                                            content: evt.reply,
                                            toolsUsed: evt.tools_used,
                                            timestamp: new Date(),
                                            messageId: evt.message_id,
                                        }]);
                                    }
                                    if (evt.client_actions && Array.isArray(evt.client_actions)) {
                                        executeActionsSequentially(evt.client_actions);
                                    }

                                } else if (evt.type === 'interrupt') {
                                    if (evt.thread_id) setThreadId(evt.thread_id);
                                    setInterruptData(evt.interrupt_data);
                                    setEditedContent(evt.interrupt_data?.content ?? '');
                                    setShowInterruptDialog(true);
                                    setMessages((prev: Message[]) => [...prev, {
                                        role: 'system',
                                        content: `⏸️ Workflow paused for review: ${evt.interrupt_data?.instruction}`,
                                        timestamp: new Date(),
                                    }]);

                                } else if (evt.type === 'error') {
                                    throw new Error(evt.message || 'Stream error');
                                }
                            } catch (parseErr) {
                                if (parseErr instanceof Error && parseErr.message !== 'Stream error') continue;
                                throw parseErr;
                            }
                        }
                    }
                } finally {
                    reader.releaseLock();
                }
                return; // streaming handled, skip JSON path below
            }

            // Non-streaming (JSON) path — existing behaviour
            const data = await response.json();

            if (data.status === 'interrupt') {
                setThreadId(data.thread_id);
                setInterruptData(data.interrupt_data);
                setEditedContent(data.interrupt_data.content);
                setShowInterruptDialog(true);
                setMessages(prev => [
                    ...prev,
                    {
                        role: 'system',
                        content: `⏸️ Workflow paused for review: ${data.interrupt_data.instruction}`,
                        timestamp: new Date(),
                    },
                ]);
            } else if (data.status === 'error') {
                throw new Error(data.message);
            } else {
                if (data.thread_id) {
                    setThreadId(data.thread_id);
                }

                if (data.client_actions && Array.isArray(data.client_actions)) {
                    executeActionsSequentially(data.client_actions);
                }

                if (data.reply) {
                    const assistantMessage: Message = {
                        role: 'assistant',
                        content: data.reply,
                        toolsUsed: data.tools_used,
                        timestamp: new Date(),
                        messageId: data.message_id,
                    };
                    setMessages((prev) => [...prev, assistantMessage]);
                }
            }
        } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') return;
            console.error('Error sending message:', error);
            toast.error(
                error instanceof Error
                    ? error.message
                    : 'Failed to send message. Please try again.'
            );
        } finally {
            abortControllerRef.current = null;
            setIsLoading(false);
        }
    };

    const resumeFromInterrupt = async (editedValue: string) => {
        setIsLoading(true);
        setShowInterruptDialog(false);

        setMessages(prev => [
            ...prev,
            {
                role: 'system',
                content: '▶️ Continuing workflow with reviewed data...',
                timestamp: new Date(),
            },
        ]);
        const raw = localStorage.getItem('auth-storage') || '';
        const parsed = JSON.parse(raw);

        // access token
        const accessToken = parsed.state.token.access_token;

        const moduleID = localStorage.getItem('selectedModuleId') || '';
        const client_context = {
            module_id: moduleID,
            access_token: accessToken,
        };
        // SECURITY FIX: Removed console.log that exposed access token
        try {

            const formData = new FormData();
            formData.append('end_user_email', endUserEmail);
            formData.append('message', '');
            formData.append('thread_id', threadId!);
            formData.append('resume_value', editedValue);
            formData.append('client_context', JSON.stringify(client_context));

            const resumeController = new AbortController();
            abortControllerRef.current = resumeController;

            const response = await fetch(`${backendUrl}/chat/${adminId}`, {
                method: 'POST',
                body: formData,
                signal: resumeController.signal,
            });

            const data = await response.json();

            if (data.status === 'interrupt') {
                setThreadId(data.thread_id);
                setInterruptData(data.interrupt_data);
                setEditedContent(data.interrupt_data.content);
                setShowInterruptDialog(true);
                setMessages(prev => [
                    ...prev,
                    {
                        role: 'system',
                        content: `⏸️ Workflow paused for review: ${data.interrupt_data.instruction}`,
                        timestamp: new Date(),
                    },
                ]);
            } else if (data.status === 'error') {
                throw new Error(data.message);
            } else {
                const assistantMessage: Message = {
                    role: 'assistant',
                    content: data.reply,
                    toolsUsed: data.tools_used,
                    timestamp: new Date(),
                    messageId: data.message_id,
                };
                setMessages(prev => [...prev, assistantMessage]);
                setInterruptData(null);
            }
        } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') return;
            console.error('Error resuming:', error);
            toast.error(
                error instanceof Error
                    ? error.message
                    : 'Failed to resume workflow. Please try again.'
            );
        } finally {
            abortControllerRef.current = null;
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const handleFeedback = async (index: number, rating: number, correction?: string) => {
        const msg = messages[index];
        if (!msg.messageId) return;

        // Optimistic update
        const updatedMessages = [...messages];
        updatedMessages[index].feedbackGiven = true;
        setMessages(updatedMessages);

        try {
            // Find previous user message
            let userQuery = "";
            for (let i = index - 1; i >= 0; i--) {
                if (messages[i].role === "user") {
                    userQuery = messages[i].content;
                    break;
                }
            }

            const response = await fetch(`${backendUrl}/feedback`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    message_id: msg.messageId.toString(),
                    user_id: endUserEmail,
                    admin_id: adminId,
                    query: userQuery,
                    response: msg.content,
                    rating: rating,
                    correction: correction
                })
            });

            if (response.ok) {
                toast.success("Feedback submitted. Thank you!");
            } else {
                toast.error("Failed to submit feedback");
            }

        } catch (e) {
            console.error("Error submitting feedback:", e);
        }
    };

    // Voice recording functions
    const startRecording = async () => {
        try {
            // Check if mediaDevices is supported
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                toast.error('Your browser does not support audio recording. Please use a modern browser like Chrome, Firefox, or Edge.');
                return;
            }

            // Check for available audio input devices
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const audioInputs = devices.filter(device => device.kind === 'audioinput');

                if (audioInputs.length === 0) {
                    toast.error('No microphone found. Please connect a microphone and try again.');
                    return;
                }
            } catch (enumError) {
                console.warn('Could not enumerate devices:', enumError);
                // Continue anyway, getUserMedia will provide better error
            }

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            const audioChunks: Blob[] = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunks.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
                setAudioBlob(audioBlob);
                // Stop all tracks to release microphone
                stream.getTracks().forEach(track => track.stop());
                // Trigger transcription
                transcribeAudio(audioBlob);
            };

            mediaRecorderRef.current = mediaRecorder;
            mediaRecorder.start();
            setIsRecording(true);
            toast.info('Recording started... Click again to stop.');
        } catch (error) {
            console.error('Error accessing microphone:', error);

            // Provide specific error messages based on error type
            if (error instanceof DOMException) {
                switch (error.name) {
                    case 'NotFoundError':
                        toast.error('No microphone found. Please connect a microphone and refresh the page.');
                        break;
                    case 'NotAllowedError':
                    case 'PermissionDeniedError':
                        toast.error('Microphone access denied. Please allow microphone permissions in your browser settings.');
                        break;
                    case 'NotReadableError':
                        toast.error('Microphone is being used by another application. Please close other apps using the microphone.');
                        break;
                    case 'OverconstrainedError':
                        toast.error('Could not start recording with the current settings. Please try again.');
                        break;
                    default:
                        toast.error(`Microphone error: ${error.message || 'Unknown error'}`);
                }
            } else {
                toast.error('Failed to access microphone. Please check your device and browser settings.');
            }
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            toast.info('Processing audio...');
        }
    };

    const transcribeAudio = async (audioBlob: Blob) => {
        await performTranscription(audioBlob);
    };

    const performTranscription = async (audioBlob: Blob) => {
        setIsLoading(true);
        try {
            // Convert webm to a format more compatible with APIs if needed
            // For now, we'll try to send it directly
            const formData = new FormData();

            // Create a File object from the Blob with .webm extension
            const audioFile = new File([audioBlob], 'audio.webm', { type: 'audio/webm' });
            formData.append('file', audioFile);
            formData.append('model', 'voxtral-mini-2507');
            formData.append('language', 'en');



            const response = await fetch(`${backendUrl}/transcribe_audio`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Transcription failed with status ${response.status}`);
            }

            const data = await response.json();
            const transcription = data.text || data.transcription || '';

            if (transcription) {
                // Set the transcribed text as input
                setInput(transcription);
                toast.success('Audio transcribed successfully!');
                // Optionally, auto-send the message
                // setTimeout(() => sendMessage(), 500);
            } else {
                toast.error('No transcription received');
            }
        } catch (error) {
            console.error('Error transcribing audio:', error);
            toast.error(
                error instanceof Error
                    ? error.message
                    : 'Failed to transcribe audio. Please try again.'
            );
        } finally {
            setIsLoading(false);
            setAudioBlob(null);
        }
    };



    const handleToggleRecording = () => {
        if (isRecording) {
            stopRecording();
        } else {
            startRecording();
        }
    };

    const renderContent = () => {
        switch (accessStatus) {
            case 'checking':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <CircularProgress />
                    </Box>
                );
            case 'none':
                return (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <MailIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>Request Access</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Enter your email to request access to this chatbot.
                        </Typography>
                        <TextField
                            fullWidth
                            label="Email Address"
                            variant="outlined"
                            value={endUserEmail}
                            onChange={(e) => setEndUserEmail(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleRequestAccess()}
                            sx={{ mb: 2 }}
                        />
                        <Button variant="contained" onClick={() => handleRequestAccess()} disabled={isLoading}>
                            {isLoading ? 'Requesting...' : 'Request Access'}
                        </Button>
                    </Box>
                );
            case 'pending':
                return (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <ClockIcon sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>Pending Approval</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Your access request for <Typography component="span" fontWeight="bold">{endUserEmail}</Typography> has been submitted.
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                            Please check back later.
                        </Typography>
                    </Box>
                );
            case 'approved':
                return (
                    <>
                        {/* Messages Area */}
                        <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: '#f5f5f5' }}>
                            {messages.length === 0 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center' }}>
                                    <Avatar sx={{ width: 64, height: 64, mb: 2, bgcolor: 'primary.light' }}><ChatIcon fontSize="large" /></Avatar>
                                    <Typography variant="h6" gutterBottom>Start a conversation</Typography>
                                    <Typography variant="body2" color="text.secondary">Ask anything or upload files for analysis</Typography>
                                </Box>
                            )}
                            {messages.map((message, index) => {
                                // Filter out intermediate action system messages
                                // Only show system messages for workflow interrupts/pauses
                                if (message.role === 'system') {
                                    const isActionMessage =
                                        message.content.startsWith('🔄') || // Navigating
                                        message.content.startsWith('👀') || // Highlighting
                                        message.content.startsWith('✍️') || // Filling form
                                        message.content.startsWith('⚠️') || // Warnings
                                        message.content.startsWith('❌');   // Errors

                                    // Only show interrupt/workflow control messages
                                    if (isActionMessage) {
                                        return null; // Don't render action messages
                                    }
                                }

                                return (
                                    <Box key={index} sx={{ display: 'flex', justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start', mb: 2 }}>
                                        {message.role === 'system' ? (
                                            <Chip
                                                icon={message.content.startsWith('⏸️') ? <PauseIcon /> : <PlayIcon />}
                                                label={message.content.substring(3)}
                                                variant="outlined"
                                                color="warning"
                                                sx={{ p: 1, height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal' } }}
                                            />
                                        ) : (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: message.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                                                <Paper
                                                    sx={{
                                                        p: 1.5,
                                                        bgcolor: message.role === 'user' ? 'primary.main' : 'white',
                                                        color: message.role === 'user' ? 'white' : 'text.primary',
                                                        background: message.role === 'user' ? `linear-gradient(135deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)` : undefined,
                                                    }}
                                                    elevation={2}
                                                >
                                                    <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                                        <Markdown>{message.content}</Markdown>
                                                    </Typography>
                                                    {(message.fileNames && message.fileNames.length > 0) && (
                                                        <Box sx={{ mt: 1 }}>
                                                            {message.fileNames.map((name, i) => (
                                                                <Chip key={i} label={name} size="small" icon={<AttachFileIcon />} sx={{ mr: 0.5, mt: 0.5 }} />
                                                            ))}
                                                        </Box>
                                                    )}
                                                </Paper>

                                                {/* Feedback UI */}
                                                {message.role === 'assistant' && message.messageId && !message.feedbackGiven && (
                                                    <Box sx={{ mt: 0.5, width: '100%' }}>
                                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', opacity: 0.5, '&:hover': { opacity: 1 }, transition: 'opacity 0.2s' }}>
                                                            <Tooltip title="Good response">
                                                                <IconButton size="small" onClick={() => handleFeedback(index, 1)} sx={{ width: 24, height: 24 }}>
                                                                    <ThumbUpIcon sx={{ fontSize: 14 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Needs improvement">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => {
                                                                        if (correctionInputId === index) {
                                                                            setCorrectionInputId(null);
                                                                        } else {
                                                                            setCorrectionInputId(index);
                                                                            setCorrectionValue("");
                                                                        }
                                                                    }}
                                                                    sx={{ width: 24, height: 24 }}
                                                                >
                                                                    <ThumbDownIcon sx={{ fontSize: 14 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>

                                                        {correctionInputId === index && (
                                                            <Fade in={true}>
                                                                <Box sx={{ mt: 1, display: 'flex', gap: 1, maxWidth: '100%' }}>
                                                                    <TextField
                                                                        size="small"
                                                                        placeholder="How should I have answered?"
                                                                        value={correctionValue}
                                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCorrectionValue(e.target.value)}
                                                                        autoFocus
                                                                        fullWidth
                                                                        onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                                            if (e.key === 'Enter' && correctionValue.trim()) {
                                                                                handleFeedback(index, 0, correctionValue);
                                                                                setCorrectionInputId(null);
                                                                            }
                                                                        }}
                                                                        sx={{ fontSize: '0.875rem' }}
                                                                    />
                                                                    <Button
                                                                        size="small"
                                                                        variant="contained"
                                                                        disabled={!correctionValue.trim()}
                                                                        onClick={() => {
                                                                            handleFeedback(index, 0, correctionValue);
                                                                            setCorrectionInputId(null);
                                                                        }}
                                                                        sx={{ minWidth: 60 }}
                                                                    >
                                                                        Send
                                                                    </Button>
                                                                </Box>
                                                            </Fade>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                            {isLoading && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                                    <Paper sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }} elevation={2}>
                                        <CircularProgress size={16} />
                                        <Typography variant="body2" color="text.secondary">Thinking...</Typography>
                                    </Paper>
                                </Box>
                            )}
                            <div ref={messagesEndRef} />
                        </Box>
                        {/* Input Area */}
                        <Box
                            sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: isDragging ? 'action.hover' : 'background.paper' }}
                            onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
                        >
                            {files.length > 0 && (
                                <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {files.map((file, index) => (
                                        <Chip key={index} label={file.name} size="small" onDelete={() => removeFile(index)} icon={<AttachFileIcon />} />
                                    ))}
                                </Box>
                            )}
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                                <input type="file" ref={fileInputRef} onChange={handleFileSelect} style={{ display: 'none' }} multiple />
                                <Tooltip title="Attach files"><IconButton size="small" onClick={() => fileInputRef.current?.click()} color="primary"><AttachFileIcon /></IconButton></Tooltip>
                                <Tooltip title={isRecording ? "Stop recording" : "Record voice message"}>
                                    <IconButton
                                        size="small"
                                        onClick={handleToggleRecording}
                                        color={isRecording ? "error" : "primary"}
                                        sx={isRecording ? {
                                            '@keyframes pulse': {
                                                '0%, 100%': {
                                                    opacity: 1,
                                                    transform: 'scale(1)',
                                                },
                                                '50%': {
                                                    opacity: 0.7,
                                                    transform: 'scale(1.1)',
                                                },
                                            },
                                            animation: 'pulse 1.5s infinite',
                                        } : {}}
                                    >
                                        {isRecording ? <StopIcon /> : <MicIcon />}
                                    </IconButton>
                                </Tooltip>
                                <TextField fullWidth multiline maxRows={4} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyPress} placeholder="Type your message..." disabled={isLoading} size="small" variant="outlined" />
                                {isLoading ? (
                                    <Tooltip title="Cancel request">
                                        <IconButton
                                            onClick={handleCancelRequest}
                                            sx={{ bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                                        >
                                            <StopIcon />
                                        </IconButton>
                                    </Tooltip>
                                ) : (
                                    <Tooltip title="Send message">
                                        <span>
                                            <IconButton onClick={sendMessage} disabled={!input.trim() && files.length === 0} color="primary" sx={{ background: `linear-gradient(135deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`, color: 'white', '&:hover': { background: `linear-gradient(135deg, ${colors.primaryDark} 0%, ${colors.secondaryDark} 100%)` }, '&.Mui-disabled': { background: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' } }}>
                                                <SendIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                            </Box>
                            {isDragging && <Typography variant="caption" color="primary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>📎 Drop files here to attach</Typography>}
                        </Box>
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <Zoom in={!isOpen}>
                <Fab data-chat-widget="true" color="primary" sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000, background: `linear-gradient(135deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`, '&:hover': { background: `linear-gradient(135deg, ${colors.primaryDark} 0%, ${colors.secondaryDark} 100%)` } }} onClick={() => setIsOpen(true)}>
                    <ChatIcon />
                </Fab>
            </Zoom>

            <Fade in={isOpen}>
                <Card data-chat-widget="true" sx={{ position: 'fixed', bottom: 24, right: 24, width: 400, height: 600, zIndex: 1000, display: isOpen ? 'flex' : 'none', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', borderRadius: 2 }}>
                    <Box sx={{ background: `linear-gradient(135deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`, color: 'white', p: 2, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}><BotIcon /></Avatar>
                                <Box>
                                    <Typography variant="h6" fontWeight={600}>AI Assist</Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.9 }}>Online • Ready to help</Typography>
                                </Box>
                            </Box>
                            <Box>
                                <Tooltip title="Clear Chat">
                                    <IconButton size="small" onClick={handleClearChat} sx={{ color: 'white', mr: 1 }}>
                                        <ClearIcon />
                                    </IconButton>
                                </Tooltip>
                                <IconButton size="small" onClick={() => setIsOpen(false)} sx={{ color: 'white' }}>
                                    <MinimizeIcon />
                                </IconButton>
                            </Box>
                        </Box>
                        {/* Available tools UI removed */}
                    </Box>

                    {renderContent()}
                </Card>
            </Fade>

            <InterruptReviewDialog
                open={showInterruptDialog}
                onClose={() => setShowInterruptDialog(false)}
                interruptData={interruptData}
                editedContent={editedContent}
                onEditedContentChange={setEditedContent}
                onContinue={resumeFromInterrupt}
            />


        </>
    );
};